import {
  UserType,
  type ChatRoom,
  type ChatRoomActivity,
  type ChatRoomWorkflowActivity,
  type UserChatRoom,
} from "@/types/chat";

const markParticipantRead = (
  participant: UserChatRoom,
  readAt: string
): UserChatRoom =>
  participant.isSelf ? { ...participant, lastReadAt: readAt } : participant;

export const markChatRoomRead = (
  rooms: ChatRoom[],
  roomId: string,
  readAt = new Date().toISOString()
): ChatRoom[] =>
  rooms.map((room) => {
    if (room.id !== roomId) return room;

    return {
      ...room,
      unreadCount: 0,
      ...(room.notificationUnreadCount !== undefined
        ? { notificationUnreadCount: 0 }
        : {}),
      currentUser: room.currentUser
        ? { ...room.currentUser, lastReadAt: readAt }
        : room.currentUser,
      userChatRooms: room.userChatRooms.map((participant) =>
        markParticipantRead(participant, readAt)
      ),
    };
  });

export const applyChatRoomActivity = (
  rooms: ChatRoom[],
  activity: ChatRoomActivity,
  selectedRoomId?: string,
  currentUserId?: string | null
): { rooms: ChatRoom[]; found: boolean } => {
  let found = false;
  const updated = rooms.map((room) => {
    if (room.id !== activity.roomId) return room;
    found = true;
    const selfParticipant =
      room.currentUser ??
      room.userChatRooms.find((participant) => participant.isSelf);
    const isOwnMessage =
      activity.lastMessage.senderType === UserType.STAFF &&
      (selfParticipant?.userId === activity.lastMessage.senderId ||
        currentUserId === activity.lastMessage.senderId);
    const isOpen = selectedRoomId === activity.roomId;
    const isDuplicate = room.lastMessage?.id === activity.lastMessage.id;
    return {
      ...room,
      lastMessage: activity.lastMessage,
      updatedAt: activity.updatedAt,
      workflowStatus:
        activity.workflowStatus !== undefined
          ? activity.workflowStatus
          : room.workflowStatus,
      workflowVersion:
        activity.workflowVersion !== undefined
          ? activity.workflowVersion
          : room.workflowVersion,
      assignedStaffId:
        activity.assignedStaffId !== undefined
          ? activity.assignedStaffId
          : room.assignedStaffId,
      assignedStaff:
        activity.assignedStaff !== undefined
          ? activity.assignedStaff
          : room.assignedStaff,
      unreadCount:
        isOwnMessage || isOpen || isDuplicate
          ? room.unreadCount
          : room.unreadCount + 1,
    };
  });

  updated.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  return { rooms: updated, found };
};

export const applyChatRoomWorkflowActivity = (
  rooms: ChatRoom[],
  activity: ChatRoomWorkflowActivity
): ChatRoom[] =>
  rooms.map((room) =>
    room.id === activity.roomId
      ? {
          ...room,
          workflowStatus: activity.workflowStatus,
          workflowVersion: activity.workflowVersion,
          assignedStaffId: activity.assignedStaffId,
          assignedStaff: activity.assignedStaff,
        }
      : room
  );
